import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload, Loader, X } from 'lucide-react';
import PlatformSelector from './PlatformSelector.jsx';
import TikTokSettings from '../connections/TikTokSettings.jsx';

// Custom TikTok icon component
const TikTokIcon = ({ size = 24, className = '' }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="currentColor" 
    className={className}
  >
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

function UploadTab({ 
  video, 
  videoPreview, 
  title, 
  description, 
  platforms, 
  connectedPlatforms,
  uploadStatus,
  isUploading,
  onVideoChange,
  onClearVideo,
  onTitleChange,
  onDescriptionChange,
  onTogglePlatform,
  onUpload,
  formatFileSize,
  getStatusIcon,
  facebookPages,
  selectedFacebookPage,
  onSelectFacebookPage,
  facebookUserName,
  youtubeChannel,
  tiktokCreatorInfo,
  tiktokSettings,
  onTiktokSettingsChange
}) {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const [tiktokConsent, setTiktokConsent] = React.useState(false);

  const handleClearClick = (e) => {
    e.preventDefault();
    // reset file input value so same file can be reselected later
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (onClearVideo) onClearVideo();
  };
  // TikTok video duration check
  const tiktokMaxDuration = tiktokCreatorInfo?.max_video_post_duration_sec || null;
  const isTikTokSelected = platforms?.tiktok && connectedPlatforms?.tiktok;
  const isTikTokDurationExceeded = isTikTokSelected && tiktokMaxDuration && video && video.duration && video.duration > tiktokMaxDuration;
  const [showProcessingInfo, setShowProcessingInfo] = React.useState(false);

  const handleUpload = (e) => {
    if (isTikTokDurationExceeded) {
      alert(t('app.upload.errors.durationExceededAlert', { minutes: Math.floor(tiktokMaxDuration / 60) }));
      return;
    }
    if (isTikTokSelected && !tiktokConsent) {
      alert(t('app.upload.errors.acceptTiktokTerms'));
      return;
    }
    setShowProcessingInfo(true);
    if (onUpload) onUpload(e);
  };

  // Compute TikTok publish disabled from settings directly
  const isTikTokSettingsIncomplete = isTikTokSelected && (
    !tiktokSettings?.privacyLevel || 
    (tiktokSettings?.brandContentToggle && !tiktokSettings?.yourBrand && !tiktokSettings?.brandedContent)
  );

  // Upload button should be disabled when TikTok settings are incomplete
  const isUploadDisabled = isUploading || !video || !title || 
    isTikTokSettingsIncomplete || 
    (isTikTokSelected && !tiktokConsent) ||
    isTikTokDurationExceeded;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {Object.values(connectedPlatforms).some(Boolean) ? (
        <PlatformSelector
          platforms={platforms}
          connectedPlatforms={connectedPlatforms}
          uploadStatus={uploadStatus}
          onTogglePlatform={onTogglePlatform}
          getStatusIcon={getStatusIcon}
          facebookPages={facebookPages}
          selectedFacebookPage={selectedFacebookPage}
          onSelectFacebookPage={onSelectFacebookPage}
          facebookUserName={facebookUserName}
          youtubeChannel={youtubeChannel}
          tiktokCreatorInfo={tiktokCreatorInfo}
        />
      ) : (
        <div className="bg-white rounded-xl shadow-sm p-6 flex items-center justify-center">
          <span className="text-gray-600 text-center">{t('app.platformSelector.noConnectedPlatforms')}</span>
        </div>
      )}

      <div className="lg:col-span-2 bg-white rounded-xl shadow-sm p-6">
        {isTikTokDurationExceeded && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded">
            <span className="text-red-700">{t('app.upload.errors.durationExceeded', { minutes: Math.floor(tiktokMaxDuration / 60) })}</span>
          </div>
        )}
        {showProcessingInfo && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded">
            <span className="text-blue-700">{t('app.upload.processingInfo')}</span>
          </div>
        )}
        <h2 className="text-lg font-bold text-gray-800 mb-4">{t('app.upload.title')}</h2>

        <div className="mb-6">
          <label className="block text-sm font-semibold text-gray-700 mb-2">{t('app.upload.videoFile')}</label>
          <input
            ref={fileInputRef}
            type="file"
            accept="video/*"
            onChange={onVideoChange}
            className="hidden"
            id="video-upload"
          />
          <label
            htmlFor="video-upload"
            className="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-sky-500 hover:bg-sky-50 transition-all"
          >
            {videoPreview ? (
              <div className="relative flex flex-col items-center">
                <video src={videoPreview} className="h-32 rounded-lg mb-2" controls />
                <button
                  onClick={handleClearClick}
                  className="absolute top-1 right-1 bg-white/90 rounded-full p-1 hover:bg-red-50 border"
                  title={t('app.upload.clearVideo')}
                >
                  <X size={16} className="text-red-600" />
                </button>
                <p className="text-sm text-gray-600 font-medium">{video.name}</p>
                <p className="text-xs text-gray-500">{formatFileSize(video.size)}</p>
              </div>
            ) : (
              <>
                <Upload className="text-gray-400 mb-2" size={48} />
                <p className="text-gray-600 font-medium">{t('app.upload.uploadPrompt')}</p>
                <p className="text-xs text-gray-500 mt-1">{t('app.upload.fileRequirements')}</p>
              </>
            )}
          </label>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-semibold text-gray-700 mb-2">{t('app.upload.titleLabel')} *</label>
          <input
            type="text"
            value={title}
            onChange={onTitleChange}
            placeholder={t('app.upload.titlePlaceholder')}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent outline-none"
          />
        </div>

        <div className="mb-6">
          <label className="block text-sm font-semibold text-gray-700 mb-2">{t('app.upload.descriptionLabel')}</label>
          <textarea
            value={description}
            onChange={onDescriptionChange}
            placeholder={t('app.upload.descriptionPlaceholder')}
            rows="4"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent outline-none resize-none"
          />
        </div>

        {/* TikTok Settings - Show when TikTok is selected */}
        {isTikTokSelected && tiktokCreatorInfo && (
          <div className="mb-6">
            <TikTokSettings
              creatorInfo={tiktokCreatorInfo}
              settings={tiktokSettings}
              onSettingsChange={onTiktokSettingsChange}
              videoType="video"
            />
          </div>
        )}

        {/* TikTok Settings Summary near video preview */}
        {isTikTokSelected && tiktokSettings && tiktokSettings.privacyLevel && (
          <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
            <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
              <TikTokIcon size={16} className="mr-2" />
              {t('app.upload.tiktokSummary.title')}
            </h4>
            <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
              <div>
                <span className="font-medium">{t('app.upload.tiktokSummary.account')}:</span>{' '}
                {tiktokCreatorInfo?.creator_nickname || tiktokCreatorInfo?.creator_username || 'N/A'}
              </div>
              <div>
                <span className="font-medium">{t('app.upload.tiktokSummary.visibility')}:</span>{' '}
                {tiktokSettings.privacyLevel === 'PUBLIC_TO_EVERYONE' && t('app.tiktok.privacy.public')}
                {tiktokSettings.privacyLevel === 'MUTUAL_FOLLOW_FRIENDS' && t('app.tiktok.privacy.friends')}
                {tiktokSettings.privacyLevel === 'SELF_ONLY' && t('app.tiktok.privacy.private')}
                {tiktokSettings.privacyLevel === 'FOLLOWER_OF_CREATOR' && t('app.tiktok.privacy.followers')}
              </div>
              <div>
                <span className="font-medium">{t('app.upload.tiktokSummary.comments')}:</span>{' '}
                {tiktokSettings.disableComment ? t('app.tiktok.toggle.off') : t('app.tiktok.toggle.on')}
              </div>
              <div>
                <span className="font-medium">{t('app.upload.tiktokSummary.duetStitch')}:</span>{' '}
                {tiktokSettings.disableDuet && tiktokSettings.disableStitch ? t('app.tiktok.toggle.off') : t('app.tiktok.toggle.on')}
              </div>
              {tiktokSettings.brandContentToggle && (
                <div className="col-span-2">
                  <span className="font-medium">{t('app.upload.tiktokSummary.commercial')}:</span>{' '}
                  {tiktokSettings.yourBrand && tiktokSettings.brandedContent && t('app.tiktok.commercial.paidPartnership')}
                  {tiktokSettings.yourBrand && !tiktokSettings.brandedContent && t('app.tiktok.commercial.promotional')}
                  {!tiktokSettings.yourBrand && tiktokSettings.brandedContent && t('app.tiktok.commercial.paidPartnership')}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Point 5c: Explicit TikTok consent checkbox - Required for express user consent */}
        {isTikTokSelected && (
          <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
            <label className="flex items-start cursor-pointer">
              <input
                type="checkbox"
                checked={tiktokConsent}
                onChange={(e) => setTiktokConsent(e.target.checked)}
                className="mr-2 mt-1 w-4 h-4"
              />
              <span className="text-xs text-gray-700">
                {tiktokSettings?.brandedContent ? (
                  <>
                    {t('app.upload.tiktokConsentBrandedPrefix')}{' '}
                    <a 
                      href="https://www.tiktok.com/legal/page/global/bc-policy/en" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      Branded Content Policy
                    </a>
                    {' '}{t('app.upload.and')}{' '}
                    <a 
                      href="https://www.tiktok.com/legal/page/global/music-usage-confirmation/en" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      Music Usage Confirmation
                    </a>
                    {' '}{t('app.upload.tiktokTermsSuffix')}
                  </>
                ) : (
                  <>
                    {t('app.upload.tiktokConsentPrefix')}{' '}
                    <a 
                      href="https://www.tiktok.com/legal/page/global/music-usage-confirmation/en" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      Music Usage Confirmation
                    </a>
                    {' '}{t('app.upload.tiktokTermsSuffix')}
                  </>
                )}
              </span>
            </label>
          </div>
        )}

        {/* Upload button — wrapped in span to allow title tooltip even when disabled */}
        <span
          className="block"
          title={
            isTikTokSelected && tiktokSettings?.brandContentToggle && !tiktokSettings?.yourBrand && !tiktokSettings?.brandedContent
              ? t('app.tiktok.needContentType')
              : undefined
          }
        >
        <button
          onClick={handleUpload}
          disabled={isUploadDisabled}
          className={`w-full py-4 rounded-xl font-semibold text-white text-lg transition-all ${
            isUploadDisabled
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-gradient-to-r from-sky-500 from-10% to-emerald-500 to-90% hover:from-indigo-700 hover:to-sky-700 shadow-lg hover:shadow-xl'
          }`}
        >
          {isUploading ? (
            <span className="flex items-center justify-center">
              <Loader className="animate-spin mr-2" size={20} />
              {t('app.upload.uploading')}
            </span>
          ) : (
            t('app.upload.uploadButton')
          )}
        </button>
        </span>
      </div>
    </div>
  );
}

export default UploadTab;